/**
 * NOT CHECKED
 */
import React, { useState } from 'react';

import Table, { Tr, Th, Td, TBody, THead } from '@uikit/old/Table/Table';

type RenderCellPropsType = {
  row: any;
  dataKey?: string;
  renderProps?: any;
  rowIndex?: number;
  isExpanded?: boolean;
};

export type TableConfigType = {
  title?: string;
  dataKey: string;
  RenderTitle?: (any) => JSX.Element;
  RenderCell?: (props: RenderCellPropsType) => JSX.Element;
  cellClassName?: string;
  thCellClassName?: string;
  colspan?: number;
}[];

type ConfigurableTablePropsType = {
  tableRef?: any;
  config: TableConfigType;
  data: any[];
  renderProps?: {
    [propKey: string]: any;
  };
  availableColumns?: string[];
  rowClassName?: string;
  tableClassName?: string;
  RenderRow?: (any) => JSX.Element;
  onRowClick?: (any) => void;
  renderSubRow?: (row: any) => JSX.Element;
  isRowExpandable?: (row: any) => boolean;
  /** data-testid on the <table> (for ui-automation-tests) */
  testId?: string;
  /**
   * Builds a data-testid for each row. The default row renders it on its <tr>; a custom
   * RenderRow receives it as the `rowTestId` prop and must put it on its own <tr>.
   */
  rowTestId?: (row: { [key: string]: unknown }) => string;
};

function TableHeader({ columns, renderProps, onePageItems }): JSX.Element {
  return (
    <THead>
      <Tr>
        {columns.map(({ RenderTitle, title, dataKey, colspan, isHidden, thCellClassName = '' }) =>
          isHidden ? null : (
            <Th key={`th-${dataKey}`} className={thCellClassName} colspan={colspan}>
              {RenderTitle ? (
                <RenderTitle
                  renderProps={{ dataKey, ...renderProps }}
                  onePageItems={onePageItems}
                />
              ) : (
                title
              )}
            </Th>
          )
        )}
      </Tr>
    </THead>
  );
}

const DefaultRenderCell = ({ row, dataKey }: RenderCellPropsType): JSX.Element =>
  row[dataKey] !== undefined ? row[dataKey] : '';

function DefaultTableRow({ children, rowClassName, onRowClick, rowTestId }): JSX.Element {
  return (
    <Tr className={rowClassName} onClick={onRowClick} data-testid={rowTestId}>
      {children}
    </Tr>
  );
}

function ConfigurableTable({
  tableRef,
  config,
  data,
  renderProps = {},
  availableColumns,
  rowClassName = '',
  tableClassName = '',
  onRowClick,
  RenderRow = DefaultTableRow,
  renderSubRow,
  isRowExpandable,
  testId,
  rowTestId,
}: ConfigurableTablePropsType): JSX.Element {
  const [expandedRowId, setExpandedRowId] = useState(null);
  const filtredColumns = availableColumns
    ? config.filter(({ dataKey }) => availableColumns.includes(dataKey))
    : config;

  const toggleRow = (rowId) => {
    setExpandedRowId((prev) => (prev === rowId ? null : rowId));
  };

  return (
    <Table tableRef={tableRef} className={tableClassName} data-testid={testId}>
      <TableHeader columns={filtredColumns} renderProps={renderProps} onePageItems={data} />
      <TBody>
        {data?.map((dataRow, index: number) => {
          const isExpanded = expandedRowId === dataRow.id;

          return (
            <React.Fragment key={`fragment-${dataRow.id}-${index}`}>
              <RenderRow
                rowClassName={rowClassName}
                row={dataRow}
                renderProps={{ ...renderProps, expandedRowId }}
                columns={filtredColumns}
                rowTestId={rowTestId ? rowTestId(dataRow) : undefined}
                onRowClick={
                  isRowExpandable
                    ? () => toggleRow(dataRow.id)
                    : onRowClick
                      ? () => onRowClick(dataRow)
                      : undefined
                }
              >
                {filtredColumns.map(
                  ({ RenderCell = DefaultRenderCell, dataKey, cellClassName = '', colspan }) => (
                    <Td
                      className={cellClassName}
                      key={`td-${dataKey}-${dataRow.id}`}
                      colspan={colspan}
                    >
                      <RenderCell
                        row={dataRow}
                        dataKey={dataKey}
                        renderProps={renderProps}
                        rowIndex={index}
                        isExpanded={isExpanded}
                      />
                    </Td>
                  )
                )}
              </RenderRow>

              {/* SUB ROW */}
              {isExpanded && renderSubRow && (
                <Tr className="sub-row">
                  <Td colspan={filtredColumns.length}>{renderSubRow(dataRow)}</Td>
                </Tr>
              )}
            </React.Fragment>
          );
        })}
      </TBody>
    </Table>
  );
}

export default ConfigurableTable;
